import Card from "./Card";
import Button from "Components/Common/Button/Button";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import { useToast } from "Components/Common/Toast/ToastContext";
import { DrawDetail, fmtMoney } from "../DrawHistory.types";
import { formatDate } from "utils/Common/Date";
import EmptyState from "Components/Common/EmptyState";
import EmptyLienWaiverIcon from "Assets/Img/EmptyLienWaiverIcon";

interface WaiversTabProps {
  drawDetail: DrawDetail | null;
  loading: boolean;
}

const WaiversTab = ({ drawDetail, loading }: WaiversTabProps) => {
  const showToast = useToast();
  const items = drawDetail?.lienWaivers?.items ?? [];
  const signedCount =
    drawDetail?.lienWaivers?.signed ??
    items.filter((item) => item?.signedAt).length;

  return (
    <>
      <div className="sectionTitle">Lien Waivers</div>
      <div className="sectionSub">
        Waivers collected for Draw #
        {drawDetail
          ? String(drawDetail?.draw?.drawNumber).padStart(2, "0")
          : "—"}
        .
      </div>

      <Card title={`Waiver Status (${signedCount} of ${items?.length} signed)`}>
        {loading ? (
          Array.from({ length: 2 }).map((_, i) => (
            <div className="docItem" key={i}>
              <Skeleton width={36} height={36} borderRadius={8} />
              <div className="docBody">
                <Skeleton
                  width="60%"
                  height={13}
                  borderRadius={4}
                  style={{ marginBottom: 6 }}
                />
                <Skeleton width="40%" height={11} borderRadius={4} />
              </div>
              <Skeleton width={90} height={28} borderRadius={5} />
            </div>
          ))
        ) : items.length === 0 ? (
          <EmptyState
            noBorder
            icon={<EmptyLienWaiverIcon />}
            title="No lien waivers found for this draw."
          />
        ) : (
          items.map((item) => {
            const status = item?.signedAt
              ? "signed"
              : item?.invitedAt
                ? "pending"
                : "missing";
            const statusLabel =
              status === "signed"
                ? "✓ Signed"
                : status === "pending"
                  ? "Pending"
                  : "Not Sent";
            return (
              <div className="docItem" key={item?.id}>
                <div className={`docIcon ${status}`}>
                  {status === "signed" ? "✓" : "!"}
                </div>
                <div className="docBody">
                  <div className="docName">
                    {item?.subContractor?.companyName ??
                      item?.companyName ??
                      "—"}
                  </div>
                  <div className="docMeta">
                    {[
                      fmtMoney(item?.totalAmount),
                      item?.signedAt
                        ? `Signed ${formatDate(item?.signedAt, false)}`
                        : item?.invitedAt
                          ? `Sent ${formatDate(item?.invitedAt, false)}`
                          : "Not yet sent",
                    ]
                      .filter(Boolean)
                      .join(" · ")}{" "}
                    by {item?.companyName}
                  </div>
                </div>
                <div className={`docStatus ${status}`}>{statusLabel}</div>
                <Button
                  type="button"
                  className="docBtn"
                  onClick={() =>
                    item?.waiverDocument?.url
                      ? window.open(item?.waiverDocument?.url, "_blank")
                      : showToast(
                          status === "signed"
                            ? "Opening waiver…"
                            : "Waiver not signed yet.",
                        )
                  }
                >
                  View
                </Button>
              </div>
            );
          })
        )}
      </Card>
    </>
  );
};

export default WaiversTab;
